import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.number.constructor.js";
import "core-js/modules/es.array.find.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import React, { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import config from 'utils/config';
import track from 'utils/track';
import hasAutoCompleteEnabled from 'utils/asset/hasAutoCompleteEnabled';
import formatData from 'utils/asset/formatAssetViewerData';
import { useAssetViewerContext } from 'screens/AssetViewerUI/hooks/useAssetViewerContext';
import { useAuthContext } from '../../contexts/auth';
import { useFetchUserProfile } from 'actions/userProfile';
import { assetQueryKeys, useMarkMyAssetAsDone, useSendMyAssetFeedback } from 'actions/assets';
import { playlistQueryKeys } from 'actions/playlists';
import Box from 'components/Box';
import Landmark from 'components/Landmark';
import Comments from 'components/Comments';
import FeedbackForm from 'components/Comments/FeedbackForm';
import { usePlaylistCompletionContext } from 'components/PlaylistCompletionProvider';
import AssetViewerUI from 'screens/AssetViewerUI';
import InfoSection from 'screens/AssetViewerUI/InfoSection';
import RelatedPlaylists from 'screens/AssetViewerUI/RelatedPlaylists';
import ErrorAndLoadingWrapper from 'screens/AssetViewerUI/ErrorAndLoadingWrapper';
import AssetViewerContent from 'screens/AssetViewerUI/Content';
import { Actions, FullscreenActions } from './Actions';
import PlaylistNavigator from './PlaylistNavigator';

/**
 * The hub's Asset viewer.
 * Achtung! This won't work without being wrapped in a {@link hooks/useAssetViewerContext#Provider|Asset Context Provider}
 * @returns {element} - The wrapped AssetViewer
 */

function HubAssetViewer(_ref) {
  var _playlists$find;
  var onClose = _ref.onClose;
  var _useAssetViewerContex = useAssetViewerContext(),
    asset = _useAssetViewerContex.asset,
    downloadUrl = _useAssetViewerContex.downloadUrl,
    params = _useAssetViewerContex.params,
    error = _useAssetViewerContex.error,
    onError = _useAssetViewerContex.onError,
    onLoad = _useAssetViewerContex.onLoad,
    isReady = _useAssetViewerContex.isReady,
    markIframeContentAsLoaded = _useAssetViewerContex.markIframeContentAsLoaded;
  var assetId = Number(asset === null || asset === void 0 ? void 0 : asset.id);
  var playlistId = Number(params.playlistId);
  var queryClient = useQueryClient();
  var _useAuthContext = useAuthContext(),
    setAuthUser = _useAuthContext.setAuthUser;
  var _useFetchUserProfile = useFetchUserProfile(),
    userProfile = _useFetchUserProfile.data,
    isUserProfileLoading = _useFetchUserProfile.isLoading;
  var _useSendMyAssetFeedba = useSendMyAssetFeedback({
      assetId: assetId
    }),
    sendMyAssetFeedback = _useSendMyAssetFeedba.sendMyAssetFeedback;
  var _usePlaylistCompletio = usePlaylistCompletionContext(),
    setCompletedPlaylists = _usePlaylistCompletio.setCompletedPlaylists;
  var _useMarkMyAssetAsDone = useMarkMyAssetAsDone({
      setCompletedPlaylists: setCompletedPlaylists,
      shouldShowModalImmediately: false
    }),
    markMyAssetAsDone = _useMarkMyAssetAsDone.markMyAssetAsDone;
  useEffect(function () {
    if (assetId) {
      // Note, `track('asset', 'open', <id>)` tracks the asset-opened event
      // and also implicitly sets the asset's userContentStatus to 'opened' on the server
      track('asset', 'open', assetId);
      if ((asset === null || asset === void 0 ? void 0 : asset.userContentStatus) === 'notstarted') {
        queryClient.setQueryData(assetQueryKeys.myAsset(assetId, playlistId), function (asset) {
          return _objectSpread(_objectSpread({}, asset), {}, {
            userContentStatus: 'opened'
          });
        });
        if (playlistId) {
          queryClient.invalidateQueries(playlistQueryKeys.singlePlaylist(playlistId));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, playlistId, queryClient]);

  // TODO: consider using local guards against there being no asset
  if (!asset) return null;
  var _formatData = formatData({
      asset: asset,
      origin: 'hub'
    }),
    playlists = _formatData.playlists,
    tags = _formatData.tags;
  var shouldRenderPlaylistInfo = playlists && (playlists === null || playlists === void 0 ? void 0 : playlists.length) > 0 && !((playlists === null || playlists === void 0 ? void 0 : playlists.length) === 1 && Number(playlists[0].id) === Number(playlistId));
  var playlistName = playlists === null || playlists === void 0 ? void 0 : (_playlists$find = playlists.find(function (playlist) {
    return playlist.id === playlistId;
  })) === null || _playlists$find === void 0 ? void 0 : _playlists$find.title;
  var autoCompleteEnabled = hasAutoCompleteEnabled({
    assetType: asset.type,
    markAsDoneMode: asset === null || asset === void 0 ? void 0 : asset.markAsDoneMode
  });
  function onMarkAsDone() {
    return _onMarkAsDone.apply(this, arguments);
  }
  function _onMarkAsDone() {
    _onMarkAsDone = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (!((asset === null || asset === void 0 ? void 0 : asset.userContentStatus) !== 'completed')) {
              _context.next = 3;
              break;
            }
            _context.next = 3;
            return markMyAssetAsDone({
              id: assetId,
              playlistId: playlistId
            });
          case 3:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return _onMarkAsDone.apply(this, arguments);
  }
  var renderComments = function renderComments() {
    if (isUserProfileLoading) {
      return /*#__PURE__*/React.createElement(Landmark, {
        icon: "spinner"
      });
    } else if (config.enableComments) {
      return /*#__PURE__*/React.createElement(Box, {
        p: "m"
      }, /*#__PURE__*/React.createElement(Comments, {
        contentId: assetId,
        totalCount: asset.commentCount
      }));
    } else {
      return /*#__PURE__*/React.createElement(FeedbackForm, {
        userProfile: userProfile,
        onSubmit: sendMyAssetFeedback
      });
    }
  };
  return /*#__PURE__*/React.createElement(AssetViewerUI, {
    asset: asset,
    onClose: onClose,
    autoCompleteEnabled: autoCompleteEnabled,
    renderContent: function renderContent() {
      return /*#__PURE__*/React.createElement(ErrorAndLoadingWrapper, {
        isReady: isReady,
        error: error
      }, /*#__PURE__*/React.createElement(AssetViewerContent, {
        asset: asset,
        playlistId: playlistId,
        downloadUrl: downloadUrl,
        markIframeContentAsLoaded: markIframeContentAsLoaded,
        onError: onError,
        onLoad: onLoad,
        autoCompleteEnabled: autoCompleteEnabled,
        onComplete: onMarkAsDone,
        setAuthUser: setAuthUser
      }));
    },
    renderInfoSection: function renderInfoSection() {
      return /*#__PURE__*/React.createElement(InfoSection, {
        description: asset.description,
        tags: tags
      });
    },
    renderPlaylistInfo: function renderPlaylistInfo() {
      return shouldRenderPlaylistInfo && /*#__PURE__*/React.createElement(RelatedPlaylists, {
        playlists: playlists,
        playlistId: playlistId
      });
    },
    renderActions: function renderActions() {
      return /*#__PURE__*/React.createElement(Actions, {
        autoCompleteEnabled: autoCompleteEnabled
      });
    },
    renderFullscreenActions: function renderFullscreenActions() {
      return /*#__PURE__*/React.createElement(FullscreenActions, null);
    },
    renderComments: renderComments,
    renderNavigator: function renderNavigator() {
      return Boolean(playlistId) && playlistName && /*#__PURE__*/React.createElement(PlaylistNavigator, {
        assetId: assetId,
        playlistId: playlistId,
        playlistName: playlistName
      });
    }
  });
}
export default HubAssetViewer;